import React, { useState, useEffect, useRef } from 'react';
import './NewQuestModal.css';
import PlaceFields from './PlaceFields';
import useFileDrop from '../hooks/useFileDrop';

// Parse import text into a normalised { locations, questInfo, extraKeys }.
// Accepts either a bare array of points or the editor's export shape
// (an object with a `locations` array, optionally a `quest_info`). `extraKeys`
// lists any top-level keys other than `locations` — the "add to current quest"
// mode rejects a payload that carries them, since it must be locations-only.
const parseImport = (text) => {
  const parsed = JSON.parse(text); // throws on malformed JSON

  if (Array.isArray(parsed)) {
    return { locations: parsed, questInfo: undefined, extraKeys: [] };
  }
  if (parsed && typeof parsed === 'object') {
    if (!Array.isArray(parsed.locations)) {
      throw new Error('"locations" must be an array.');
    }
    const questInfo =
      parsed.quest_info && typeof parsed.quest_info === 'object' && !Array.isArray(parsed.quest_info)
        ? parsed.quest_info
        : undefined;
    const extraKeys = Object.keys(parsed).filter((k) => k !== 'locations');
    return { locations: parsed.locations, questInfo, extraKeys };
  }
  throw new Error('Expected an object with "locations", or a bare array of points.');
};

// Import a quest from JSON. Two modes:
//   * "New quest"       — create a fresh quest; the operator types id, city,
//                         country and (as a fallback) a name.
//   * "Add to current"  — append the points to the currently open quest; the
//                         id/name/city fields are locked and the JSON must
//                         contain only locations.
// The JSON can be attached as a file or pasted directly.
const ImportQuestModal = ({ onClose, onImport, onAppend, currentQuest }) => {
  const [mode, setMode] = useState('new'); // 'new' | 'append'
  const [source, setSource] = useState('file'); // 'file' | 'paste'

  const [name, setName] = useState('');
  const [questId, setQuestId] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  // Russian names / flag code for a place the dictionary does not know yet.
  const [names, setNames] = useState({});

  const [pasteText, setPasteText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (firstFieldRef.current) firstFieldRef.current.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Feed parsed JSON in from either source. Clears/sets error and count.
  const ingest = (text, label) => {
    setError('');
    setParsed(null);
    try {
      const result = parseImport(text);
      setParsed(result);
      setFileName(label || '');
      // Surface the name that lives in the file (if any) so the operator sees
      // it; the Name field only applies in new-quest mode when it is absent.
      const jsonName = result.questInfo && result.questInfo.quest_name;
      if (jsonName && !name.trim()) setName(String(jsonName));
    } catch (err) {
      setError(`Could not read this JSON: ${err.message}`);
    }
  };

  const handleFile = (e) => readFile(e.target.files && e.target.files[0]);

  const readFile = (file) => {
    setFileName('');
    if (!file) {
      setParsed(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => ingest(String(reader.result), file.name);
    reader.onerror = () => setError('Could not read this file.');
    reader.readAsText(file);
  };

  const { isDragOver, dropProps } = useFileDrop((files) => readFile(files[0]));

  const handlePaste = (e) => {
    const text = e.target.value;
    setPasteText(text);
    if (text.trim()) {
      ingest(text, 'pasted JSON');
    } else {
      setParsed(null);
      setError('');
    }
  };

  const handleSubmit = async () => {
    if (!parsed) {
      setError(source === 'file' ? 'Pick a JSON file first.' : 'Paste some JSON first.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      if (mode === 'append') {
        if (parsed.extraKeys.length > 0) {
          throw new Error('In "add to current quest" mode the JSON must contain only locations.');
        }
        await onAppend({ questId: currentQuest.id, locations: parsed.locations });
      } else {
        const trimmedId = questId.trim();
        const trimmedCity = city.trim();
        const trimmedCountry = country.trim();
        if (!trimmedId) throw new Error('Specify an id for the quest.');
        if (/\s/.test(trimmedId)) throw new Error('Id must not contain spaces.');
        if (!trimmedCity) throw new Error('Specify a city.');
        if (!trimmedCountry) throw new Error('Specify a country.');

        const questData = {
          quest_info: parsed.questInfo || {},
          locations: parsed.locations,
        };
        await onImport({
          questId: trimmedId,
          city: trimmedCity,
          country: trimmedCountry,
          ...names,
          questName: name.trim(),
          questData,
        });
      }
      // The parent closes the modal on success.
    } catch (err) {
      setError(err.message || 'Failed to import the quest.');
      setSubmitting(false);
    }
  };

  const pointCount = parsed ? parsed.locations.length : 0;
  const onlyLocationsViolation = mode === 'append' && parsed && parsed.extraKeys.length > 0;

  return (
    <div className="new-quest-overlay" onMouseDown={onClose}>
      <div className="new-quest-dialog" onMouseDown={(e) => e.stopPropagation()}>
        <h2>Import Quest</h2>

        {/* New quest vs. append to the currently open quest */}
        <div className="new-quest-toggle">
          <button
            type="button"
            className={mode === 'new' ? 'active' : ''}
            onClick={() => { setMode('new'); setError(''); }}
          >
            New quest
          </button>
          <button
            type="button"
            className={mode === 'append' ? 'active' : ''}
            disabled={!currentQuest}
            title={currentQuest ? '' : 'Open a quest first'}
            onClick={() => { setMode('append'); setError(''); }}
          >
            Add to current
          </button>
        </div>

        {mode === 'append' && currentQuest && (
          <p className="new-quest-label" style={{ fontWeight: 400, marginTop: 12 }}>
            Points will be appended to the end of{' '}
            <strong>{currentQuest.name}</strong>. The JSON must contain only <code>locations</code>.
          </p>
        )}

        {/* File vs. paste */}
        <div className="new-quest-toggle">
          <button
            type="button"
            className={source === 'file' ? 'active' : ''}
            onClick={() => { setSource('file'); setError(''); }}
          >
            Attach file
          </button>
          <button
            type="button"
            className={source === 'paste' ? 'active' : ''}
            onClick={() => { setSource('paste'); setError(''); }}
          >
            Paste JSON
          </button>
        </div>

        {source === 'file' ? (
          <label className={isDragOver ? 'new-quest-field is-drop-target' : 'new-quest-field'} {...dropProps}>
            <span className="new-quest-label">JSON file (pick or drop here)</span>
            <input
              ref={mode === 'append' ? firstFieldRef : null}
              type="file"
              accept=".json,application/json"
              onChange={handleFile}
            />
          </label>
        ) : (
          <label className="new-quest-field">
            <span className="new-quest-label">JSON</span>
            <textarea
              className="new-quest-textarea"
              value={pasteText}
              onChange={handlePaste}
              placeholder={'{\n  "locations": [\n    { "name": "Point 1", "coordinates": [41.0096, 28.9573] }\n  ]\n}'}
              spellCheck={false}
            />
          </label>
        )}

        {parsed && (
          <span
            className="new-quest-label"
            style={{ fontWeight: 400, color: onlyLocationsViolation ? '#c0392b' : '#27ae60', marginTop: 8 }}
          >
            {fileName ? `${fileName} — ` : ''}{pointCount} {pointCount === 1 ? 'point' : 'points'}
            {onlyLocationsViolation ? ' · extra keys not allowed here' : ''}
          </span>
        )}

        {mode === 'new' ? (
          <>
            <label className="new-quest-field">
              <span className="new-quest-label">Id <small>(no spaces)</small></span>
              <input
                ref={source === 'file' ? firstFieldRef : null}
                type="text"
                value={questId}
                onChange={(e) => { setQuestId(e.target.value); setError(''); }}
                placeholder="unordinary-istanbul"
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
              />
            </label>

            <PlaceFields
              city={city}
              country={country}
              onCityChange={(v) => { setCity(v); setError(''); }}
              onCountryChange={(v) => { setCountry(v); setError(''); }}
              names={names}
              onNamesChange={(patch) => { setNames(n => ({ ...n, ...patch })); setError(''); }}
            />

            <label className="new-quest-field">
              <span className="new-quest-label">Name <small>(optional — used only if the file has none)</small></span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Unordinary Istanbul"
              />
            </label>
          </>
        ) : (
          currentQuest && (
            <>
              <label className="new-quest-field">
                <span className="new-quest-label">Quest</span>
                <div className="new-quest-readonly">{currentQuest.name}</div>
              </label>
              <label className="new-quest-field">
                <span className="new-quest-label">Id</span>
                <div className="new-quest-readonly">{currentQuest.id}</div>
              </label>
              {currentQuest.city && (
                <label className="new-quest-field">
                  <span className="new-quest-label">City</span>
                  <div className="new-quest-readonly">{currentQuest.city}</div>
                </label>
              )}
            </>
          )
        )}

        {error && <div className="new-quest-error">{error}</div>}

        <div className="new-quest-actions">
          <button onClick={onClose} className="new-quest-cancel" disabled={submitting}>
            Cancel
          </button>
          <button onClick={handleSubmit} className="new-quest-create" disabled={submitting}>
            {submitting
              ? (mode === 'append' ? 'Adding…' : 'Importing…')
              : (mode === 'append' ? 'Add points' : 'Import')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportQuestModal;
